import { Injectable, signal, computed, effect, untracked, inject, Injector } from '@angular/core';
import { UsersFilter } from '../filters/users.filter';
import { ListConstants } from '../constants/lists.constants';
import { OrganizationsFilter } from '../filters/organizations.filter';
import { MembersFilter } from '../filters/members.filter';
import { OrganizationModel } from '../models/data/organization.model';
import { PropertiesFilter } from '../filters/properties.filter';
import { PropertyChargeSchedulesFilter } from '../filters/property-charge-schedule.filter';
import { BudgetsFilter } from '../filters/budgets.filter';
import { ExpenseFilter } from '../filters/expense.filter';
import { SentEmailFilter } from '../filters/sent-email.filter';
import { PaymentHistoryFilter } from '../filters/payment-history.filter';
import { AuthService } from '@auth0/auth0-angular';
import { UserModel } from '../models/data/user.model';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserService } from './user.service';
import { MemberService } from './member.service';
import { AuthorizationStateService } from './authorization/authorization-state.service';
import { CacheService } from './cache.service';
import { GuardConstants, LocalStorageKeyConstants } from '../constants/constants';
import { ServiceUtilities } from '../utilities/service.utilities';
import { OrganizationSettingModel } from '../models/data/organization-setting.model';
import { OrganizationService } from './organization.service';
import { AddressModel } from '../models/data/address.model';
import { ChartOfAccountModel } from '../models/data/chart-of-account.model';
import { StripeConnectionResponse } from '../models/service/stripe-connect.model';
import { environment } from '../../../environments/environment';
import { CurrencyModel } from '../models/data/currency.model';
import { LocalizationConstants } from '../constants/settings.constants';
import { FeatureFlag, FeatureFlagLoadState } from '../enums/feature-flags.enums';
import { FeatureFlagMap } from '../models/data/feature-flag.model';
import { FeatureFlagUtilities } from '../utilities/feature-flag.utilities';
import { OrganizationScopedResource } from '../utilities/organization-scoped-resource';

@Injectable({
  providedIn: 'root'
})
export class UserSessionService {

  public activeOrganizationLocalStorageKey = (userId: number): string => `membershine_active_organization_user_${userId}`;

  private organizationService = inject(OrganizationService);
  private memberService = inject(MemberService);
  private injector = inject(Injector);

  // Lazy lookup — AuthorizationStateService constructor-injects UserSessionService,
  // so eager DI here would be circular. Resolved on first effect run instead.
  //
  // Composite `${userId}:${orgId}` key: this service is a root singleton, so orgId
  // alone would permanently skip a subsequent user's login into the same org after
  // a logout/login-swap in the same tab.
  private _recordedLoginKey: string | undefined = undefined;

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private cacheService: CacheService
  ) {
    this.loadFromLocalStorage();

    effect(() => {
      const orgId = this.activeOrganizationId();

      if (orgId !== undefined && untracked(() => this.hasInitializedOrganization())) {
        this.cacheService.clearOrganizationData();
      }

      if (orgId !== undefined) {
        this.hasInitializedOrganization.set(true);
        this.hasNoActiveOrganizations.set(false);
      }
    });

    effect(() => {
      const org = this.activeOrganization();
      const user = untracked(() => this.activeUser());

      if (user && org) {
        this.saveToLocalStorage(this.activeOrganizationLocalStorageKey(user.userId), org);
      }
    });

    effect(() => {
      const orgId = this.activeOrganizationId();
      untracked(() => {
        this.activeOrganizationSettings.set(undefined);
        this._settingsLoadedForOrgId = undefined;

        // Reset to Pending rather than to an empty map: see FeatureFlagUtilities for why the two
        // are not the same answer.
        this.featureFlagsResource.invalidate();
        this.featureFlagsLoadState.set(FeatureFlagLoadState.Pending);
      });

      // Untracked because both loaders read activeOrganization() synchronously: without it this
      // effect depends on the whole organization object, so an in-place name edit re-runs it and
      // flickers every flag-gated element back through Pending.
      if (orgId !== undefined) {
        untracked(() => {
          this.getOrganizationSettings();
          void this.getFeatureFlags();
        });
      }
    });

    // Addresses and the Stripe connection are only needed by a handful of surfaces (e.g.
    // the default country on the Property form, the mark-as-paid dialog, bank reconciliation), so an
    // org switch invalidates the cached copies without refetching them — the next consumer that calls
    // getOrganizationAddresses() / getStripeConnection() / getStripeBalanceAccount() triggers the load.
    effect(() => {
      this.activeOrganizationId();

      untracked(() => {
        this.addressesResource.invalidate();

        this.activeOrganizationStripeConnection.set(undefined);
        this.activeOrganizationStripeBalanceAccount.set(undefined);
        this._stripeBalanceAccountLoadedForOrgId = undefined;
      });
    });

    // Member.LastLogin tracking: fires once per (userId, orgId) after the authorization
    // state has loaded, covering both initial login (localStorage-restored or
    // switcher-default) and later org switches. Staff users with no member record for
    // the active org are silently skipped in recordMemberLogin, and the guard is only
    // marked after a real update fires so a later authState refresh (e.g. a membership
    // added mid-session) still gets recorded.
    effect(() => {
      const orgId = this.activeOrganizationId();
      const authState = this.injector.get(AuthorizationStateService).authorizationState();

      if (orgId === undefined || !authState) return;

      untracked(() => this.recordMemberLogin(orgId));
    });
  }

  private recordMemberLogin(organizationId: number): void {
    const userId = this.activeUser()?.userId;
    if (userId === undefined) return;

    const key = `${userId}:${organizationId}`;
    if (this._recordedLoginKey === key) return;

    const authState = this.injector.get(AuthorizationStateService).authorizationState();
    const member = authState?.memberPermissions?.find(m => m.organizationId === organizationId);
    if (!member) return;

    this._recordedLoginKey = key;
    void this.memberService.updateMemberLastLogin(member.memberId);
  }

  public hasInitializedOrganization = signal<boolean>(false);

  // True when the user's initial organization load returned zero active organizations
  // they are an active member of. Only ever set for non-staff users (staff have access
  // to all organizations via their user-level permissions).
  public hasNoActiveOrganizations = signal<boolean>(false);

  public activeOrganization = signal<OrganizationModel | undefined>(undefined);

  // Tracks only the org ID so "did the user switch orgs?" consumers (cache eviction,
  // settings refetch) don't fire when the same org's fields change in place — e.g. a
  // name edit from the profile page calling handleOrganizationUpdated().
  public activeOrganizationId = computed<number | undefined>(() => this.activeOrganization()?.organizationId);

  public activeUser = signal<UserModel | undefined>(undefined);

  //#region Organization Settings (lazy-loaded)
  public activeOrganizationSettings = signal<OrganizationSettingModel | undefined>(undefined);
  private _settingsLoadedForOrgId: number | undefined = undefined;
  private _settingsLoadingPromise: Promise<OrganizationSettingModel | undefined> | undefined = undefined;

  // Reactive currency accessors for the active organization. Today both fall back to safe defaults when
  // settings haven't loaded; switching to throw-on-missing is a single-file change here.
  public currentCurrency = computed<CurrencyModel | undefined>(() => this.activeOrganizationSettings()?.currency);
  public currentMinorUnits = computed<number>(() => this.currentCurrency()?.minorUnits ?? LocalizationConstants.DEFAULT_MINOR_UNITS());

  public async getOrganizationSettings(): Promise<OrganizationSettingModel | undefined> {
    const currentOrg = this.activeOrganization();
    
    if (!currentOrg) {
      return undefined;
    }

    if (this._settingsLoadedForOrgId === currentOrg.organizationId && this.activeOrganizationSettings()) {
      return this.activeOrganizationSettings();
    }

    if (this._settingsLoadingPromise) {
      return this._settingsLoadingPromise;
    }

    this._settingsLoadingPromise = this.fetchOrganizationSettings(currentOrg.organizationId);
    
    try {
      const settings = await this._settingsLoadingPromise;
      return settings;
    } finally {
      this._settingsLoadingPromise = undefined;
    }
  }

  private async fetchOrganizationSettings(organizationId: number): Promise<OrganizationSettingModel | undefined> {
    try {
      const result = await this.organizationService.getOrganizationSettings(organizationId);
      
      if (result.success && result.data) {
        this.activeOrganizationSettings.set(result.data);
        this._settingsLoadedForOrgId = organizationId;
        return result.data;
      } else {
        console.error('Failed to load organization settings:', result.message);
        return undefined;
      }
    } catch (error) {
      console.error('Error fetching organization settings:', error);
      return undefined;
    }
  }

  public async refreshOrganizationSettings(): Promise<OrganizationSettingModel | undefined> {
    const currentOrg = this.activeOrganization();
    
    if (!currentOrg) {
      return undefined;
    }

    this._settingsLoadedForOrgId = undefined;
    this.activeOrganizationSettings.set(undefined);

    return this.getOrganizationSettings();
  }
  //#endregion

  //#region Feature Flags (eager, loaded on organization switch)
  private readonly featureFlagsResource = new OrganizationScopedResource<FeatureFlagMap>(
    'organization feature flags',
    () => this.activeOrganizationId(),
    async organizationId => {
      const result = await this.organizationService.getOrganizationFeatureFlags(organizationId);

      if (result.success && result.data) {
        return result.data.flags ?? {};
      }

      console.error('Failed to load organization feature flags:', result.message);
      return undefined;
    },
    loaded => this.featureFlagsLoadState.set(loaded ? FeatureFlagLoadState.Loaded : FeatureFlagLoadState.Failed)
  );

  public activeOrganizationFeatureFlags = this.featureFlagsResource.value;
  public featureFlagsLoadState = signal<FeatureFlagLoadState>(FeatureFlagLoadState.Pending);

  public isFeatureEnabled(flag: FeatureFlag): boolean {
    return FeatureFlagUtilities.isEnabled(this.activeOrganizationFeatureFlags(), this.featureFlagsLoadState(), flag);
  }

  public isFeatureBlocked(flag: FeatureFlag): boolean {
    return FeatureFlagUtilities.isBlocked(this.activeOrganizationFeatureFlags(), this.featureFlagsLoadState(), flag);
  }

  public getFeatureFlags(): Promise<FeatureFlagMap | undefined> {
    return this.featureFlagsResource.load();
  }
  //#endregion

  //#region Organization Addresses (lazy-loaded)
  // Addresses don't come back on the organization payloads the switcher and localStorage hold, so
  // they are fetched on demand with includeAddresses and cached per organization.
  private readonly addressesResource = new OrganizationScopedResource<AddressModel[]>(
    'organization addresses',
    () => this.activeOrganizationId(),
    async organizationId => {
      const result = await this.organizationService.getOrganizationById(organizationId, { includeAddresses: true });

      if (result.success && result.data) {
        return result.data.addresses ?? [];
      }

      console.error('Failed to load organization addresses:', result.message);
      return undefined;
    }
  );

  public activeOrganizationAddresses = this.addressesResource.value;

  public getOrganizationAddresses(): Promise<AddressModel[] | undefined> {
    return this.addressesResource.load();
  }
  //#endregion

  //#region Stripe Connection (lazy-loaded)
  public activeOrganizationStripeConnection = signal<StripeConnectionResponse | undefined>(undefined);
  public activeOrganizationStripeBalanceAccount = signal<ChartOfAccountModel | undefined>(undefined);
  private _stripeBalanceAccountLoadedForOrgId: number | undefined = undefined;
  private _stripeBalanceAccountLoadingPromise: Promise<ChartOfAccountModel | undefined> | undefined = undefined;
  private _stripeBalanceAccountLoadingOrgId: number | undefined = undefined;

  // Bank reconciliation needs to know whether Stripe onboarding is done before it can offer to
  // connect a bank account, and it needs the balance account to keep it out of the GL picker. Both
  // ride along on the same payload, so it is cached whole and the two accessors share one fetch.
  public async getStripeConnection(): Promise<StripeConnectionResponse | undefined> {
    await this.getStripeBalanceAccount();
    return this.activeOrganizationStripeConnection();
  }

  // The organization's Stripe balance GL account only rides along on the Stripe connection
  // payload, so surfaces that merely display it (the mark-as-paid dialog) fetch it on demand
  // and cache it per organization rather than pulling the connection on every open.
  public async getStripeBalanceAccount(): Promise<ChartOfAccountModel | undefined> {
    const currentOrg = this.activeOrganization();

    if (!currentOrg) {
      return undefined;
    }

    const organizationId = currentOrg.organizationId;

    if (this._stripeBalanceAccountLoadedForOrgId === organizationId) {
      return this.activeOrganizationStripeBalanceAccount();
    }

    if (this._stripeBalanceAccountLoadingPromise && this._stripeBalanceAccountLoadingOrgId === organizationId) {
      return this._stripeBalanceAccountLoadingPromise;
    }

    const loadingPromise = this.fetchStripeBalanceAccount(organizationId);
    this._stripeBalanceAccountLoadingPromise = loadingPromise;
    this._stripeBalanceAccountLoadingOrgId = organizationId;

    try {
      return await loadingPromise;
    } finally {
      if (this._stripeBalanceAccountLoadingPromise === loadingPromise) {
        this._stripeBalanceAccountLoadingPromise = undefined;
        this._stripeBalanceAccountLoadingOrgId = undefined;
      }
    }
  }

  private async fetchStripeBalanceAccount(organizationId: number): Promise<ChartOfAccountModel | undefined> {
    const result = await this.organizationService.getStripeConnection(organizationId);

    // Drop a response that arrives after the user switched organizations rather than
    // caching one organization's account as the active organization's.
    if (this.activeOrganization()?.organizationId !== organizationId) {
      return undefined;
    }

    if (!result.success || !result.data) {
      console.error('Failed to load organization Stripe balance account:', result.message);
      return undefined;
    }

    const account = result.data.stripeBalanceAccount;
    this.activeOrganizationStripeConnection.set(result.data);
    this.activeOrganizationStripeBalanceAccount.set(account);
    this._stripeBalanceAccountLoadedForOrgId = organizationId;
    return account;
  }
  //#endregion

  public async setActiveUser(): Promise<void> {
    try {
      const helper = new JwtHelperService();
      const token = await this.authService.getAccessTokenSilently().toPromise();
      
      if (!token) {
        this.activeUser.set(undefined);
        return;
      }

      const decodedToken = helper.decodeToken(token);
      if (environment.isDevelopment) {
        console.log(token);
      }
      
      const userId = decodedToken.membershine_user_id;
      
      if (userId) {
        // A transient blip here would otherwise strand a user with a valid Auth0 token on
        // /system/auth-error, since userLoadedGuard gates the whole dashboard on this call. getUserById
        // swallows its errors into the result, so the retry keys off that rather than a throw.
        const result = await ServiceUtilities.retryTransient(
          () => this.userService.getUserById(userId),
          GuardConstants.AUTH_LOAD_MAX_RETRIES(),
          GuardConstants.AUTH_LOAD_RETRY_BASE_DELAY_MS(),
          'UserSessionService.setActiveUser'
        );

        // Leaving activeUser unset is what the guard reads as "could not load the user"; returning here
        // rather than falling through keeps that an explicit exit instead of a TypeError on the
        // localStorage key below.
        if (!result.success || !result.data) {
          this.activeUser.set(undefined);
          return;
        }

        const user = result.data;
        this.activeUser.set(user);

        const savedOrg = this.getFromLocalStorage<OrganizationModel>(
          this.activeOrganizationLocalStorageKey(user.userId)
        );
        if (savedOrg) {
          this.activeOrganization.set(savedOrg);
        }
      } else {
        this.activeUser.set(undefined);
      }
    } catch (error) {
      console.error('Error setting active user:', error);
      this.activeUser.set(undefined);
      throw error;
    }
  }

  public updateActiveUser(user: UserModel): void {
    this.activeUser.set(user);
  }

  public isCurrentUser(userId: number): boolean {
    return this.activeUser()?.userId === userId;
  }

  // Called by surfaces that edit an organization (profile settings, contacts, addresses,
  // avatar, etc.) so the active-org signal — and downstream consumers like the org
  // switcher label — reflect the new server state without forcing a page reload.
  public handleOrganizationUpdated(updated: OrganizationModel): void {
    if (this.activeOrganization()?.organizationId === updated.organizationId) {
      this.activeOrganization.set(updated);

      // The addresses surfaces PATCH and hand back the full organization, so refresh the
      // cached copy here instead of leaving consumers on a pre-edit snapshot.
      if (updated.addresses) {
        this.addressesResource.prime(updated.organizationId, updated.addresses);
      }
    }
  }

  // Removing the persisted org matters: setActiveUser() restores it blindly on login,
  // so a stale entry for a since-deactivated org would otherwise be re-restored on
  // every future login.
  public markNoActiveOrganizations(): void {
    const user = this.activeUser();
    if (user) {
      localStorage.removeItem(this.activeOrganizationLocalStorageKey(user.userId));
    }
    this.activeOrganization.set(undefined);
    this.hasNoActiveOrganizations.set(true);
  }

  //#region User list 
  private _usersFilter: UsersFilter = ListConstants.USERS_FILTER_DEFAULT();
  public get usersFilter(): UsersFilter {
    return this._usersFilter;
  }
  public set usersFilter(value: UsersFilter) {
    this._usersFilter = value;
  }
    
  private _usersOrderBy: string = ListConstants.USERS_ORDERBY_DEFAULT();
  public get usersOrderBy(): string {
    return this._usersOrderBy;
  }
  public set usersOrderBy(value: string) {
    this._usersOrderBy = value;
  }
  //#endregion

  //#region Organization list 
  private _organizationsFilter: OrganizationsFilter = ListConstants.ORGANIZATIONS_FILTER_DEFAULT();
  public get organizationsFilter(): OrganizationsFilter {
    return this._organizationsFilter;
  }
  public set organizationsFilter(value: OrganizationsFilter) {
    this._organizationsFilter = value;
  }
    
  private _organizationsOrderBy: string = ListConstants.ORGANIZATIONS_ORDERBY_DEFAULT();
  public get organizationsOrderBy(): string {
    return this._organizationsOrderBy;
  }
  public set organizationsOrderBy(value: string) {
    this._organizationsOrderBy = value;
  }
  //#endregion

  //#region Member list
  private _membersFilter: MembersFilter = ListConstants.MEMBERS_FILTER_DEFAULT();
  public get membersFilter(): MembersFilter {
    return this._membersFilter;
  }
  public set membersFilter(value: MembersFilter) {
    this._membersFilter = value;
  }

  private _membersOrderBy: string = ListConstants.MEMBERS_ORDERBY_DEFAULT();
  public get membersOrderBy(): string {
    return this._membersOrderBy;
  }
  public set membersOrderBy(value: string) {
    this._membersOrderBy = value;
  }
  //#endregion

  //#region Property list
  private _propertiesFilter: PropertiesFilter = ListConstants.PROPERTIES_FILTER_DEFAULT();
  public get propertiesFilter(): PropertiesFilter {
    return this._propertiesFilter;
  }
  public set propertiesFilter(value: PropertiesFilter) {
    this._propertiesFilter = value;
  }

  private _propertiesOrderBy: string = ListConstants.PROPERTIES_ORDERBY_DEFAULT();
  public get propertiesOrderBy(): string {
    return this._propertiesOrderBy;
  }
  public set propertiesOrderBy(value: string) {
    this._propertiesOrderBy = value;
  }
  //#endregion

  //#region Property Charge Schedule list
  private _propertyChargeSchedulesFilter: PropertyChargeSchedulesFilter = ListConstants.PROPERTY_CHARGE_SCHEDULES_FILTER_DEFAULT();
  public get propertyChargeSchedulesFilter(): PropertyChargeSchedulesFilter {
    return this._propertyChargeSchedulesFilter;
  }
  public set propertyChargeSchedulesFilter(value: PropertyChargeSchedulesFilter) {
    this._propertyChargeSchedulesFilter = value;
  }

  private _propertyChargeSchedulesOrderBy: string = ListConstants.PROPERTY_CHARGE_SCHEDULES_ORDERBY_DEFAULT();
  public get propertyChargeSchedulesOrderBy(): string {
    return this._propertyChargeSchedulesOrderBy;
  }
  public set propertyChargeSchedulesOrderBy(value: string) {
    this._propertyChargeSchedulesOrderBy = value;
  }
  //#endregion

  //#region Budget list
  private _budgetsFilter: BudgetsFilter = ListConstants.BUDGETS_FILTER_DEFAULT();
  public get budgetsFilter(): BudgetsFilter {
    return this._budgetsFilter;
  }
  public set budgetsFilter(value: BudgetsFilter) {
    this._budgetsFilter = value;
  }

  private _budgetsOrderBy: string = ListConstants.BUDGETS_ORDERBY_DEFAULT();
  public get budgetsOrderBy(): string {
    return this._budgetsOrderBy;
  }
  public set budgetsOrderBy(value: string) {
    this._budgetsOrderBy = value;
  }
  //#endregion

  //#region Payment History list
  private _paymentHistoryFilter: PaymentHistoryFilter = ListConstants.PAYMENT_HISTORY_FILTER_DEFAULT();
  public get paymentHistoryFilter(): PaymentHistoryFilter {
    return this._paymentHistoryFilter;
  }
  public set paymentHistoryFilter(value: PaymentHistoryFilter) {
    this._paymentHistoryFilter = value;
  }

  private _paymentHistoryOrderBy: string = ListConstants.PAYMENT_HISTORY_ORG_VIEW_ORDERBY_DEFAULT();
  public get paymentHistoryOrderBy(): string {
    return this._paymentHistoryOrderBy;
  }
  public set paymentHistoryOrderBy(value: string) {
    this._paymentHistoryOrderBy = value;
  }
  //#endregion

  //#region Expenses list
  private _expensesFilter: ExpenseFilter = ListConstants.EXPENSES_FILTER_DEFAULT();
  public get expensesFilter(): ExpenseFilter {
    return this._expensesFilter;
  }
  public set expensesFilter(value: ExpenseFilter) {
    this._expensesFilter = value;
  }

  private _expensesOrderBy: string = ListConstants.EXPENSES_ORDERBY_DEFAULT();
  public get expensesOrderBy(): string {
    return this._expensesOrderBy;
  }
  public set expensesOrderBy(value: string) {
    this._expensesOrderBy = value;
  }
  //#endregion

  //#region Sent Emails list
  private _sentEmailsFilter: SentEmailFilter = ListConstants.SENT_EMAILS_FILTER_DEFAULT();
  public get sentEmailsFilter(): SentEmailFilter {
    return this._sentEmailsFilter;
  }
  public set sentEmailsFilter(value: SentEmailFilter) {
    this._sentEmailsFilter = value;
  }

  private _sentEmailsOrderBy: string = ListConstants.SENT_EMAILS_ORDERBY_DEFAULT();
  public get sentEmailsOrderBy(): string {
    return this._sentEmailsOrderBy;
  }
  public set sentEmailsOrderBy(value: string) {
    this._sentEmailsOrderBy = value;
  }
  //#endregion

  public resetFilters(): void {
    this.usersFilter = ListConstants.USERS_FILTER_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.USERS_FILTER(), this.usersFilter);
    
    this.usersOrderBy = ListConstants.USERS_ORDERBY_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.USERS_ORDERBY(), this.usersOrderBy);
    
    this.organizationsFilter = ListConstants.ORGANIZATIONS_FILTER_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.ORGANIZATIONS_FILTER(), this.organizationsFilter);
    
    this.organizationsOrderBy = ListConstants.ORGANIZATIONS_ORDERBY_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.ORGANIZATIONS_ORDERBY(), this.organizationsOrderBy);
    
    this.membersFilter = ListConstants.MEMBERS_FILTER_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.MEMBERS_FILTER(), this.membersFilter);
    
    this.membersOrderBy = ListConstants.MEMBERS_ORDERBY_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.MEMBERS_ORDERBY(), this.membersOrderBy);
    
    this.propertiesFilter = ListConstants.PROPERTIES_FILTER_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.PROPERTIES_FILTER(), this.propertiesFilter);
    
    this.propertiesOrderBy = ListConstants.PROPERTIES_ORDERBY_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.PROPERTIES_ORDERBY(), this.propertiesOrderBy);
    
    this.propertyChargeSchedulesFilter = ListConstants.PROPERTY_CHARGE_SCHEDULES_FILTER_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.PROPERTY_CHARGE_SCHEDULES_FILTER(), this.propertyChargeSchedulesFilter);
    
    this.propertyChargeSchedulesOrderBy = ListConstants.PROPERTY_CHARGE_SCHEDULES_ORDERBY_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.PROPERTY_CHARGE_SCHEDULES_ORDERBY(), this.propertyChargeSchedulesOrderBy);

    this.budgetsFilter = ListConstants.BUDGETS_FILTER_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.BUDGETS_FILTER(), this.budgetsFilter);

    this.budgetsOrderBy = ListConstants.BUDGETS_ORDERBY_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.BUDGETS_ORDERBY(), this.budgetsOrderBy);

    this.paymentHistoryFilter = ListConstants.PAYMENT_HISTORY_FILTER_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.PAYMENT_HISTORY_FILTER(), this.paymentHistoryFilter);

    this.paymentHistoryOrderBy = ListConstants.PAYMENT_HISTORY_ORG_VIEW_ORDERBY_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.PAYMENT_HISTORY_ORDERBY(), this.paymentHistoryOrderBy);

    this.expensesFilter = ListConstants.EXPENSES_FILTER_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.EXPENSES_FILTER(), this.expensesFilter);

    this.expensesOrderBy = ListConstants.EXPENSES_ORDERBY_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.EXPENSES_ORDERBY(), this.expensesOrderBy);

    this.sentEmailsFilter = ListConstants.SENT_EMAILS_FILTER_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.SENT_EMAILS_FILTER(), this.sentEmailsFilter);

    this.sentEmailsOrderBy = ListConstants.SENT_EMAILS_ORDERBY_DEFAULT();
    this.saveToLocalStorage(LocalStorageKeyConstants.SENT_EMAILS_ORDERBY(), this.sentEmailsOrderBy);
  }

  //#region Local Storage Management
  public saveToLocalStorage(key: string, value: any): void {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
    } catch (error) {
      console.error(`Error saving to local storage (${key}):`, error);
    }
  }

  private loadFromLocalStorage(): void {
    try {
      const usersFilter = this.getFromLocalStorage<UsersFilter>(LocalStorageKeyConstants.USERS_FILTER());
      if (usersFilter) this._usersFilter = usersFilter;

      const usersOrderBy = this.getFromLocalStorage<string>(LocalStorageKeyConstants.USERS_ORDERBY());
      if (usersOrderBy) this._usersOrderBy = usersOrderBy;

      const organizationsFilter = this.getFromLocalStorage<OrganizationsFilter>(LocalStorageKeyConstants.ORGANIZATIONS_FILTER());
      if (organizationsFilter) this._organizationsFilter = organizationsFilter;

      const organizationsOrderBy = this.getFromLocalStorage<string>(LocalStorageKeyConstants.ORGANIZATIONS_ORDERBY());
      if (organizationsOrderBy) this._organizationsOrderBy = organizationsOrderBy;

      const membersFilter = this.getFromLocalStorage<MembersFilter>(LocalStorageKeyConstants.MEMBERS_FILTER());
      if (membersFilter) this._membersFilter = membersFilter;

      const membersOrderBy = this.getFromLocalStorage<string>(LocalStorageKeyConstants.MEMBERS_ORDERBY());
      if (membersOrderBy) this._membersOrderBy = membersOrderBy;

      const propertiesFilter = this.getFromLocalStorage<PropertiesFilter>(LocalStorageKeyConstants.PROPERTIES_FILTER());
      if (propertiesFilter) this._propertiesFilter = propertiesFilter;

      const propertiesOrderBy = this.getFromLocalStorage<string>(LocalStorageKeyConstants.PROPERTIES_ORDERBY());
      if (propertiesOrderBy) this._propertiesOrderBy = propertiesOrderBy;

      const propertyChargeSchedulesFilter = this.getFromLocalStorage<PropertyChargeSchedulesFilter>(LocalStorageKeyConstants.PROPERTY_CHARGE_SCHEDULES_FILTER());
      if (propertyChargeSchedulesFilter) this._propertyChargeSchedulesFilter = propertyChargeSchedulesFilter;

      const propertyChargeSchedulesOrderBy = this.getFromLocalStorage<string>(LocalStorageKeyConstants.PROPERTY_CHARGE_SCHEDULES_ORDERBY());
      if (propertyChargeSchedulesOrderBy) this._propertyChargeSchedulesOrderBy = propertyChargeSchedulesOrderBy;

      const budgetsFilter = this.getFromLocalStorage<BudgetsFilter>(LocalStorageKeyConstants.BUDGETS_FILTER());
      if (budgetsFilter) this._budgetsFilter = budgetsFilter;

      const budgetsOrderBy = this.getFromLocalStorage<string>(LocalStorageKeyConstants.BUDGETS_ORDERBY());
      if (budgetsOrderBy) this._budgetsOrderBy = budgetsOrderBy;

      const paymentHistoryFilter = this.getFromLocalStorage<PaymentHistoryFilter>(LocalStorageKeyConstants.PAYMENT_HISTORY_FILTER());
      if (paymentHistoryFilter) this._paymentHistoryFilter = paymentHistoryFilter;

      const paymentHistoryOrderBy = this.getFromLocalStorage<string>(LocalStorageKeyConstants.PAYMENT_HISTORY_ORDERBY());
      if (paymentHistoryOrderBy) this._paymentHistoryOrderBy = paymentHistoryOrderBy;

      const expensesFilter = this.getFromLocalStorage<ExpenseFilter>(LocalStorageKeyConstants.EXPENSES_FILTER());
      if (expensesFilter) this._expensesFilter = expensesFilter;

      const expensesOrderBy = this.getFromLocalStorage<string>(LocalStorageKeyConstants.EXPENSES_ORDERBY());
      if (expensesOrderBy) this._expensesOrderBy = expensesOrderBy;

      const sentEmailsFilter = this.getFromLocalStorage<SentEmailFilter>(LocalStorageKeyConstants.SENT_EMAILS_FILTER());
      if (sentEmailsFilter) this._sentEmailsFilter = sentEmailsFilter;

      const sentEmailsOrderBy = this.getFromLocalStorage<string>(LocalStorageKeyConstants.SENT_EMAILS_ORDERBY());
      if (sentEmailsOrderBy) this._sentEmailsOrderBy = sentEmailsOrderBy;

    } catch (error) {
      console.error('Error loading from local storage:', error);
    }
  }

  private getFromLocalStorage<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;
      return JSON.parse(item) as T;
    } catch (error) {
      return null;
    }
  }

  public clearLocalStorage(): void {
    Object.keys(localStorage)
      .filter(key => key.startsWith('membershine_active_organization_user_'))
      .forEach(key => localStorage.removeItem(key));
    
    localStorage.removeItem(LocalStorageKeyConstants.USERS_FILTER());
    localStorage.removeItem(LocalStorageKeyConstants.USERS_ORDERBY());
    localStorage.removeItem(LocalStorageKeyConstants.ORGANIZATIONS_FILTER());
    localStorage.removeItem(LocalStorageKeyConstants.ORGANIZATIONS_ORDERBY());
    localStorage.removeItem(LocalStorageKeyConstants.MEMBERS_FILTER());
    localStorage.removeItem(LocalStorageKeyConstants.MEMBERS_ORDERBY());
    localStorage.removeItem(LocalStorageKeyConstants.PROPERTIES_FILTER());
    localStorage.removeItem(LocalStorageKeyConstants.PROPERTIES_ORDERBY());
    localStorage.removeItem(LocalStorageKeyConstants.PROPERTY_CHARGE_SCHEDULES_FILTER());
    localStorage.removeItem(LocalStorageKeyConstants.PROPERTY_CHARGE_SCHEDULES_ORDERBY());
    localStorage.removeItem(LocalStorageKeyConstants.BUDGETS_FILTER());
    localStorage.removeItem(LocalStorageKeyConstants.BUDGETS_ORDERBY());

    this.activeUser.set(undefined);
    this.activeOrganization.set(undefined);
    this.hasNoActiveOrganizations.set(false);
  }

  //#endregion
}
